import { AssetGroup } from '@play-co/timestep-core';
import { View } from '@play-co/timestep-core/ui';
import Scroll from 'src/game/components/shared/Scroll';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import StateObserver, { now, user } from 'src/StateObserver';
import i18n from 'src/lib/i18n/i18n';
import { Feature } from 'src/replicant/analytics';
import {
  cardsGetActionablePendingTrades,
  cardsGetPendingTrades,
} from 'src/replicant/cards/cards.getters';
import type { CardId } from 'src/replicant/cards/cards.ruleset';
import type { CardPendingTrade } from 'src/replicant/cards/cards.schema';
import {
  CARDS_TRADE_ITEM_ASSETS,
  CardsTradeItem,
  type CardsTradeItemState,
} from '../components/CardsTradeItem';
import { cardsCollectReceived, cardsPickCardToTrade } from '../cards.sequence';

export type PopupCardTradesOpenArgs = { tradeId?: string };
export type PopupCardTradesCloseArgs = [changed?: boolean];

export default class PopupCardTrades extends PopupBasic<
  (...args: PopupCardTradesCloseArgs) => void
> {
  private scroll: Scroll<CardPendingTrade>;
  private selectedTradeId?: string;

  constructor(opts: {
    superview: View;
    close: (...args: PopupCardTradesCloseArgs) => void;
  }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: 582,
      height: 720,
      darkerBg: true,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });

    this.message.updateOpts({
      y: 360,
      width: 440,
      height: 80,
      size: 26,
      color: '#9A7171',
    });

    const scrollWidth = 535;
    this.scroll = new Scroll<CardPendingTrade>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 5,
      margin: 7,
      rect: {
        x: (this.box.style.width - scrollWidth) * 0.5,
        y: 80,
        width: scrollWidth,
        height: 600,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs(['assets/ui/cards/common']),
      ...CARDS_TRADE_ITEM_ASSETS,
    ]);
  }

  private createItem(superview: View, _: number, trade: CardPendingTrade) {
    const cardIds = this.getOverviewCardIds(trade);
    const view = new CardsTradeItem({
      superview,
    });

    view.setup({
      sendCardId: cardIds.sendCardId,
      receiveCardId: cardIds.receiveCardId,
      otherUserName: trade.otherUserName,
      otherUserAvatar: trade.otherUserAvatar,
      tradeState: this.getTradeItemState(trade),
      onAccept: () => this.acceptTrade(trade.tradeId),
      onTrade: () => this.counterTrade(trade.tradeId),
      onReject: () => this.rejectTrade(trade.tradeId),
    });

    return view;
  }

  private getTradeItemState(trade: CardPendingTrade): CardsTradeItemState {
    const isProposer = trade.proposerId === user().id;

    if (isProposer) {
      return trade.status === 'countered' ? 'sentCountered' : 'sent';
    }

    return trade.status === 'countered' ? 'receivedCountered' : 'received';
  }

  private getOverviewCardIds(trade: CardPendingTrade) {
    const isProposer = trade.proposerId === user().id;
    const offeredCardId = trade.offeredCardIds[0] as CardId | undefined;
    const counterCardId = trade.counterCardIds?.[0] as CardId | undefined;

    return {
      sendCardId: isProposer ? offeredCardId : counterCardId,
      receiveCardId: isProposer ? counterCardId : offeredCardId,
    };
  }

  private async acceptTrade(tradeId: string) {
    const changed = await StateObserver.invoke.cardsConfirmTrade({
      tradeId,
    });
    if (changed) {
      await cardsCollectReceived();
    }
    this.refreshAfterAction(!!changed);
  }

  private async rejectTrade(tradeId: string) {
    const changed = await StateObserver.invoke.cardsCancelTrade({
      tradeId,
    });
    this.refreshAfterAction(!!changed);
  }

  private async counterTrade(tradeId: string) {
    const card = await cardsPickCardToTrade();
    if (!card) return;

    const changed = await StateObserver.invoke.cardsCounterTrade({
      tradeId,
      cardId: card.id,
    });
    this.refreshAfterAction(!!changed);
  }

  private refreshAfterAction(changed: boolean) {
    const hasTrades = this.setTrades();
    if (!hasTrades) {
      this.opts.close(changed);
    }
  }

  private setTrades() {
    // Read pending and actionable trades from the same state snapshot.
    const currentUser = user();
    const currentNow = now();
    const actionableTradeIds = new Set(
      cardsGetActionablePendingTrades(currentUser, currentNow).map(
        (trade) => trade.tradeId,
      ),
    );

    // Apply the optional deep-link filter before ordering the visible list.
    const trades = cardsGetPendingTrades(currentUser, currentNow).filter(
      (trade) =>
        !this.selectedTradeId || trade.tradeId === this.selectedTradeId,
    );

    // Keep actionable trades first and preserve oldest-first order within groups.
    trades.sort(
      (a, b) =>
        Number(actionableTradeIds.has(b.tradeId)) -
        Number(actionableTradeIds.has(a.tradeId)),
    );

    if (trades.length === 0) {
      this.message.localeText = () => i18n('cards.cardTrade.none');
      this.message.show();
      this.scroll.getView().hide();
      this.scroll.getBackgroundView()?.hide();
      return false;
    }

    this.message.hide();
    this.scroll.getView().show();
    this.scroll.getBackgroundView()?.show();
    this.scroll.setItems(trades);
    return true;
  }

  public init(opts: PopupCardTradesOpenArgs) {
    super.init({
      title: i18n('cards.cardTrade.inboxTitle'),
    });

    this.selectedTradeId = opts.tradeId;
    this.setTrades();
  }
}
